/**
 * OtpPanel.jsx
 *
 * Doctor view  → "Verify OTP" button → opens panel → enter OTP → verify
 * Patient view → "View OTP"   button → opens panel → sees OTP digits
 *
 * Changes from original:
 * - keyboardOffset prop: panel slides above the virtual keyboard in WebView
 * - No "Refresh OTP" button — one OTP per appointment
 * - OTP is loaded once on mount for patient; no manual reload
 */

import React, { useState, useEffect, useCallback } from "react";
import { fetchOtp, verifyOtp } from "./otpService";

export default function OtpPanel({ appointmentId, userRole, keyboardOffset = 0, onClose }) {
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [verified, setVerified] = useState(false);
  const [otp,      setOtp]      = useState("");
  const [otpInput, setOtpInput] = useState("");

  // ── Patient: load OTP once when panel opens ───────────────────────────────
  useEffect(() => {
    if (userRole !== "patient" || !appointmentId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const code = await fetchOtp(appointmentId);
        if (!cancelled) setOtp(String(code).trim());
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load OTP. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [appointmentId, userRole]);

  // ── Doctor: verify submitted OTP ─────────────────────────────────────────
  const handleVerify = async () => {
    if (!otpInput.trim()) { setError("Please enter the OTP."); return; }
    setLoading(true);
    setError("");
    try {
      const resp = await verifyOtp(appointmentId, otpInput.trim());
      if (resp?.data === true || resp?.success === true) {
        setVerified(true);
      } else {
        setError(resp?.message || "Invalid OTP. Please check and try again.");
        setOtpInput("");
      }
    } catch (err) {
      setError(err.message || "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const digits = otp ? otp.split("") : [];

  // ── Position panel above keyboard ────────────────────────────────────────
  // bottomBase: the panel normally sits at 100px above the controls bar.
  // When keyboard is open we add keyboardOffset so it stays visible.
  const bottomBase   = 100;
  const panelBottom  = bottomBase + keyboardOffset;

  return (
    <>
      {/* Backdrop */}
      <div style={S.backdrop} onClick={onClose} />

      {/* Panel — floats above keyboard */}
      <div style={{ ...S.panel, bottom: panelBottom }}>

        {/* Close button */}
        <button style={S.closeBtn} onClick={onClose} aria-label="Close">✕</button>

        {/* ─────────── DOCTOR VIEW ─────────── */}
        {userRole === "doctor" && (
          <>
            <div style={S.iconWrap}><span style={S.bigIcon}>🔐</span></div>
            <h3 style={S.title}>Verify Patient OTP</h3>
            <p style={S.hint}>
              Ask the patient for their OTP code and enter it below to confirm the appointment.
            </p>

            {!verified ? (
              <>
                {/* Individual digit boxes */}
                <div style={S.inputRow}>
                  {[0,1,2,3,4,5].map((i) => (
                    <input
                      key={i}
                      id={`otp-box-${i}`}
                      style={{
                        ...S.digitBox,
                        borderColor: otpInput[i]
                          ? "rgba(0,180,216,0.8)"
                          : "rgba(255,255,255,0.15)",
                      }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={otpInput[i] || ""}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, "");
                        const arr = otpInput.split("");
                        arr[i] = val;
                        const next = arr.join("").slice(0, 6);
                        setOtpInput(next);
                        setError("");
                        if (val && i < 5) {
                          document.getElementById(`otp-box-${i + 1}`)?.focus();
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Backspace" && !otpInput[i] && i > 0) {
                          document.getElementById(`otp-box-${i - 1}`)?.focus();
                        }
                        if (e.key === "Enter") handleVerify();
                      }}
                      autoFocus={i === 0}
                    />
                  ))}
                </div>

                {error && <p style={S.errorText}>{error}</p>}

                <button
                  style={{ ...S.verifyBtn, ...(loading ? S.btnDisabled : {}) }}
                  onClick={handleVerify}
                  disabled={loading}
                >
                  {loading ? (
                    <span style={S.btnInner}>
                      <span style={S.btnSpinner} /> Verifying…
                    </span>
                  ) : (
                    "✔  Verify OTP"
                  )}
                </button>
              </>
            ) : (
              <>
                <div style={S.successIcon}>✅</div>
                <p style={S.successText}>OTP Verified!</p>
                <p style={S.successSub}>Appointment has been marked as completed.</p>
                <button style={S.verifyBtn} onClick={onClose}>Done</button>
              </>
            )}
          </>
        )}

        {/* ─────────── PATIENT VIEW ─────────── */}
        {userRole === "patient" && (
          <>
            <div style={S.iconWrap}><span style={S.bigIcon}>🔑</span></div>
            <h3 style={S.title}>Your Appointment OTP</h3>
            <p style={S.hint}>
              Share this code with your doctor to confirm the appointment.
            </p>

            {loading && (
              <div style={S.loadingWrap}>
                <div style={S.largeSpinner} />
                <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, marginTop: 10 }}>
                  Loading OTP…
                </p>
              </div>
            )}

            {!loading && digits.length > 0 && (
              <div style={S.otpDisplay}>
                {digits.map((d, i) => (
                  <div key={i} style={S.otpDigit}>{d}</div>
                ))}
              </div>
            )}

            {!loading && error && (
              <p style={S.errorText}>{error}</p>
            )}

            {/* No refresh button — one OTP per appointment */}
            {!loading && digits.length > 0 && (
              <p style={S.otpNote}>This OTP is valid for your entire appointment.</p>
            )}
          </>
        )}
      </div>
    </>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    zIndex: 598,
    backdropFilter: "blur(4px)",
  },
  panel: {
    position: "fixed",
    // `bottom` is set dynamically via inline style above
    left: "50%",
    transform: "translateX(-50%)",
    width: "min(92vw, 360px)",
    background: "#181818",
    border: "1px solid rgba(255,255,255,0.09)",
    borderRadius: 22,
    padding: "28px 22px 24px",
    zIndex: 599,
    boxShadow: "0 20px 60px rgba(0,0,0,0.8)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    animation: "pop-in 0.2s cubic-bezier(0.34,1.56,0.64,1)",
    // Smooth keyboard animation
    transition: "bottom 0.15s ease-out",
    willChange: "bottom",
  },
  closeBtn: {
    position: "absolute",
    top: 14,
    right: 14,
    background: "rgba(255,255,255,0.07)",
    border: "none",
    color: "rgba(255,255,255,0.5)",
    width: 30,
    height: 30,
    borderRadius: "50%",
    fontSize: 14,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 2,
  },
  bigIcon:     { fontSize: 30 },
  title: {
    color: "#fff",
    fontSize: 18,
    fontWeight: 700,
    margin: 0,
    textAlign: "center",
  },
  hint: {
    color: "rgba(255,255,255,0.5)",
    fontSize: 13,
    textAlign: "center",
    lineHeight: 1.5,
    margin: 0,
  },
  otpNote: {
    color: "rgba(255,255,255,0.35)",
    fontSize: 12,
    textAlign: "center",
    margin: 0,
    lineHeight: 1.4,
  },

  // ── Doctor: individual digit boxes ─────────────────────────────────────────
  inputRow: {
    display: "flex",
    gap: 8,
    justifyContent: "center",
    marginTop: 4,
  },
  digitBox: {
    width: 44,
    height: 52,
    background: "#242424",
    border: "1.5px solid rgba(255,255,255,0.15)",
    borderRadius: 10,
    color: "#fff",
    fontSize: 22,
    fontWeight: 700,
    textAlign: "center",
    outline: "none",
    fontFamily: "monospace",
    caretColor: "transparent",
    transition: "border-color 0.15s",
  },

  // ── Doctor: verify button ────────────────────────────────────────────────
  verifyBtn: {
    width: "100%",
    padding: "14px 0",
    borderRadius: 13,
    border: "none",
    background: "linear-gradient(135deg, #00b4d8 0%, #0077b6 100%)",
    color: "#fff",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 50,
    touchAction: "manipulation",
    marginTop: 4,
  },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  btnInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  btnSpinner: {
    display: "inline-block",
    width: 16,
    height: 16,
    border: "2px solid rgba(255,255,255,0.3)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
  },

  // ── Doctor: success ───────────────────────────────────────────────────────
  successIcon: { fontSize: 44, marginTop: 4 },
  successText: { color: "#69f0ae", fontSize: 20, fontWeight: 700, margin: 0 },
  successSub:  {
    color: "rgba(255,255,255,0.5)",
    fontSize: 13,
    textAlign: "center",
    margin: 0,
  },

  // ── Patient: OTP digit display ────────────────────────────────────────────
  otpDisplay: {
    display: "flex",
    gap: 8,
    justifyContent: "center",
    flexWrap: "wrap",
    margin: "6px 0",
  },
  otpDigit: {
    width: 46,
    height: 58,
    background: "#242424",
    border: "1.5px solid rgba(105,240,174,0.35)",
    borderRadius: 11,
    color: "#69f0ae",
    fontSize: 28,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "monospace",
    boxShadow: "0 0 12px rgba(105,240,174,0.1)",
  },

  // ── Loading ───────────────────────────────────────────────────────────────
  loadingWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "16px 0",
  },
  largeSpinner: {
    width: 36,
    height: 36,
    border: "3px solid rgba(255,255,255,0.1)",
    borderTopColor: "#69f0ae",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },

  // ── Error ─────────────────────────────────────────────────────────────────
  errorText: {
    color: "#ef5350",
    fontSize: 13,
    textAlign: "center",
    margin: 0,
    lineHeight: 1.4,
  },
};
